import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Database, Download, Loader2, ShieldCheck, FileJson, FileSpreadsheet, AlertTriangle, History, Upload, CheckCircle2, XCircle, FileWarning, FileSearch, RotateCcw, PlayCircle, ShieldAlert, Wand2, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { exportToCSV } from "@/lib/export-utils";
import { format } from "date-fns";
import { validateBackup, type ValidationReport } from "@/lib/backup-validator";
import { restoreBackup, type RestoreResult, type RestoreMode } from "@/lib/backup-restore";
import { autoFixBackup, type AutoFixReport } from "@/lib/backup-autofix";

// Tables scoped per nursery (have nursery_id) — admin can read all of them via RLS
const NURSERY_TABLES = [
  "children",
  "classes",
  "class_schedules",
  "staff",
  "staff_documents",
  "staff_permissions",
  "attendance",
  "leave_requests",
  "lessons",
  "lesson_files",
  "lesson_activities",
  "lesson_explanations",
  "lesson_ratings",
  "live_sessions",
  "session_attendance",
  "session_messages",
  "assignments",
  "assignment_submissions",
  "exams",
  "exam_questions",
  "exam_submissions",
  "exam_answers",
  "exam_results",
  "questions",
  "activity_responses",
  "child_activities",
  "child_observations",
  "child_weekly_reports",
  "game_results",
  "phonics_progress",
  "invoices",
  "invoice_items",
  "fee_templates",
  "expenses",
  "payroll_records",
  "financial_transactions",
  "monthly_payment_reports",
  "auto_invoice_log",
  "messages",
  "parent_notes",
  "notifications",
  "support_tickets",
  "ticket_replies",
  "nursery_modules",
  "nursery_subscriptions",
  "nursery_subscription_invoices",
  "subscription_requests",
] as const;

const Backup = () => {
  const { profile, roles } = useAuth();
  const isAdmin = roles.includes("admin") || roles.includes("super_admin");

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTable, setCurrentTable] = useState<string>("");
  const [stats, setStats] = useState<Record<string, number>>({});
  const [lastBackupAt, setLastBackupAt] = useState<string | null>(
    localStorage.getItem("last_backup_at")
  );

  // Validation + Restore state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validating, setValidating] = useState(false);
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [showAllIssues, setShowAllIssues] = useState(false);
  const [parsedData, setParsedData] = useState<Record<string, any[]> | null>(null);

  // Restore execution state
  const [restoreMode, setRestoreMode] = useState<RestoreMode>("dry_run");
  const [confirmText, setConfirmText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(0);
  const [restoreCurrentTable, setRestoreCurrentTable] = useState("");
  const [restoreResult, setRestoreResult] = useState<RestoreResult | null>(null);

  // Auto-fix state
  const [autoFixReport, setAutoFixReport] = useState<AutoFixReport | null>(null);
  const [autoFixing, setAutoFixing] = useState(false);
  const [rawParsed, setRawParsed] = useState<any>(null);
  const [showAllFixes, setShowAllFixes] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setValidating(true);
    setReport(null);
    setParsedData(null);
    setRestoreResult(null);
    setShowAllIssues(false);
    setAutoFixReport(null);
    setRawParsed(null);
    setShowAllFixes(false);
    try {
      const text = await file.text();
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch (err) {
        setReport({
          isValid: false,
          fileName: file.name,
          totalTables: 0,
          totalRows: 0,
          errorCount: 1,
          warningCount: 0,
          tableSummary: {},
          issues: [{ severity: "error", message: "تعذّر قراءة الملف كـ JSON صحيح" }],
        });
        return;
      }
      const result = validateBackup(parsed, file.name);

      if (result.meta?.nursery_id && nurseryId && result.meta.nursery_id !== nurseryId) {
        result.issues.unshift({
          severity: "error",
          message: `النسخة تخصّ حضانة مختلفة (${result.meta.nursery_name || result.meta.nursery_id}) — لا يمكن استعادتها هنا`,
        });
        result.errorCount++;
        result.isValid = false;
      }
      setReport(result);
      setRawParsed(parsed);
      if (result.isValid && parsed?.data) setParsedData(parsed.data);

      if (result.isValid) {
        toast.success("الملف سليم — يمكنك المتابعة بالاستعادة");
      } else {
        toast.error(`تم رصد ${result.errorCount} خطأ — يمكنك تجربة الإصلاح التلقائي`);
      }
    } catch (err: any) {
      toast.error(err.message || "فشل قراءة الملف");
    } finally {
      setValidating(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const resetReport = () => {
    setReport(null);
    setParsedData(null);
    setRestoreResult(null);
    setShowAllIssues(false);
    setConfirmText("");
    setAutoFixReport(null);
    setRawParsed(null);
    setShowAllFixes(false);
  };

  const runAutoFix = async () => {
    if (!rawParsed?.data) {
      toast.error("لا توجد بيانات للإصلاح");
      return;
    }
    setAutoFixing(true);
    try {
      // Brief async to let UI render the loader
      await new Promise((r) => setTimeout(r, 50));
      const { fixedData, report: fixReport } = autoFixBackup(rawParsed.data, {
        expectedNurseryId: nurseryId || undefined,
        generateMissingIds: true,
      });

      // Re-validate the fixed payload
      const newPayload = { ...rawParsed, data: fixedData };
      const newReport = validateBackup(newPayload, report?.fileName);

      if (newReport.meta?.nursery_id && nurseryId && newReport.meta.nursery_id !== nurseryId) {
        newReport.issues.unshift({
          severity: "error",
          message: `النسخة تخصّ حضانة مختلفة — لا يمكن استعادتها هنا`,
        });
        newReport.errorCount++;
        newReport.isValid = false;
      }

      setAutoFixReport(fixReport);
      setReport(newReport);
      setRawParsed(newPayload);
      if (newReport.isValid) {
        setParsedData(fixedData);
        toast.success(`تم تطبيق ${fixReport.totalChanges} إصلاح — الملف الآن سليم`);
      } else {
        toast.warning(
          `تم تطبيق ${fixReport.totalChanges} إصلاح، لكن لا تزال هناك ${newReport.errorCount} مشكلة غير قابلة للإصلاح تلقائيًا`
        );
      }
    } catch (err: any) {
      toast.error(err.message || "فشل تشغيل الإصلاح التلقائي");
    } finally {
      setAutoFixing(false);
    }
  };

  const runRestore = async () => {
    if (!parsedData || !nurseryId) return;
    setConfirmOpen(false);
    setRestoring(true);
    setRestoreProgress(0);
    setRestoreResult(null);
    try {
      const result = await restoreBackup(parsedData, {
        nurseryId,
        mode: restoreMode,
        onProgress: ({ table, percent }) => {
          setRestoreCurrentTable(table);
          setRestoreProgress(percent);
        },
      });
      setRestoreResult(result);
      if (restoreMode === "dry_run") {
        toast.success(`اختبار جاف: سيتم استعادة ${result.totalUpserted} سجل`);
      } else if (result.totalFailed === 0) {
        toast.success(`تمت الاستعادة بنجاح: ${result.totalUpserted} سجل`);
      } else {
        toast.warning(`تمت بنجاح جزئيًا: ${result.totalUpserted} نجاح / ${result.totalFailed} فشل`);
      }
    } catch (err: any) {
      toast.error(err.message || "فشل تنفيذ الاستعادة");
    } finally {
      setRestoring(false);
      setRestoreCurrentTable("");
      setConfirmText("");
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center" dir="rtl">
        <ShieldCheck className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
        <p className="text-muted-foreground">هذه الصفحة مخصصة لمدير الحضانة فقط.</p>
      </div>
    );
  }

  const nurseryId = profile?.nursery_id;

  const fetchAll = async (table: string) => {
    let from = 0;
    const pageSize = 1000;
    const all: any[] = [];
    while (true) {
      const { data, error } = await supabase
        .from(table as any)
        .select("*")
        .eq("nursery_id", nurseryId!)
        .range(from, from + pageSize - 1);
      if (error) {
        // Some tables may not have nursery_id directly — skip silently
        if (error.message?.includes("nursery_id")) return [];
        throw error;
      }
      if (!data || data.length === 0) break;
      all.push(...data);
      if (data.length < pageSize) break;
      from += pageSize;
    }
    return all;
  };

  const runBackup = async (asCSV = false) => {
    if (!nurseryId) {
      toast.error("لا توجد حضانة مرتبطة بحسابك");
      return;
    }
    setRunning(true);
    setProgress(0);
    setStats({});
    const backup: Record<string, any[]> = {};
    const counts: Record<string, number> = {};

    try {
      // Include nursery profile
      const { data: nurseryData } = await supabase
        .from("nurseries")
        .select("*")
        .eq("id", nurseryId)
        .maybeSingle();
      backup["nursery"] = nurseryData ? [nurseryData] : [];
      counts["nursery"] = backup["nursery"].length;

      for (let i = 0; i < NURSERY_TABLES.length; i++) {
        const t = NURSERY_TABLES[i];
        setCurrentTable(t);
        try {
          const rows = await fetchAll(t);
          backup[t] = rows;
          counts[t] = rows.length;
        } catch (e: any) {
          console.error(`Error backing up ${t}`, e);
          backup[t] = [];
          counts[t] = 0;
        }
        setProgress(Math.round(((i + 1) / NURSERY_TABLES.length) * 100));
        setStats({ ...counts });
      }

      const timestamp = format(new Date(), "yyyy-MM-dd_HH-mm");
      const nurseryName = nurseryData?.name?.replace(/[^\w\u0600-\u06FF-]/g, "_") || "nursery";

      if (asCSV) {
        // One CSV per non-empty table, zipped via individual downloads
        let downloaded = 0;
        for (const [t, rows] of Object.entries(backup)) {
          if (rows.length > 0) {
            exportToCSV(rows as any, `${nurseryName}_${t}_${timestamp}.csv`);
            downloaded++;
            await new Promise((r) => setTimeout(r, 200));
          }
        }
        toast.success(`تم تنزيل ${downloaded} ملف CSV`);
      } else {
        const payload = {
          meta: {
            nursery_id: nurseryId,
            nursery_name: nurseryData?.name,
            exported_at: new Date().toISOString(),
            version: 1,
            tables: Object.keys(counts),
            counts,
          },
          data: backup,
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `backup_${nurseryName}_${timestamp}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("تم إنشاء النسخة الاحتياطية بنجاح");
      }

      const now = new Date().toISOString();
      localStorage.setItem("last_backup_at", now);
      setLastBackupAt(now);
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "فشل إنشاء النسخة الاحتياطية");
    } finally {
      setRunning(false);
      setCurrentTable("");
    }
  };

  const totalRows = Object.values(stats).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6 max-w-5xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6 text-primary" /> النسخ الاحتياطية
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            احفظ نسخة كاملة من بيانات حضانتك في أي وقت
          </p>
        </div>
        {lastBackupAt && (
          <Badge variant="secondary" className="flex items-center gap-1">
            <History className="w-3 h-3" />
            آخر نسخة: {format(new Date(lastBackupAt), "dd/MM/yyyy HH:mm")}
          </Badge>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-primary/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileJson className="w-5 h-5 text-primary" /> نسخة كاملة (JSON)
            </CardTitle>
            <CardDescription>
              ملف واحد يحتوي على كل بيانات الحضانة. مناسب للاستعادة لاحقًا.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full"
              size="lg"
              onClick={() => runBackup(false)}
              disabled={running}
            >
              {running ? (
                <>
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" /> جاري التحضير...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 ml-2" /> تنزيل نسخة JSON
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" /> نسخة Excel/CSV
            </CardTitle>
            <CardDescription>
              ملف CSV لكل جدول — مناسب للفتح في Excel وعمل تقارير.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full"
              size="lg"
              variant="outline"
              onClick={() => runBackup(true)}
              disabled={running}
            >
              {running ? (
                <>
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" /> جاري التحضير...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 ml-2" /> تنزيل ملفات CSV
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {running && (
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">جاري نسخ: {currentTable}</span>
              <span className="text-muted-foreground">{progress}%</span>
            </div>
            <Progress value={progress} />
            <p className="text-xs text-muted-foreground text-center">
              تم نسخ {totalRows.toLocaleString("ar-EG")} سجل حتى الآن
            </p>
          </CardContent>
        </Card>
      )}

      {!running && Object.keys(stats).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">ملخص آخر نسخة</CardTitle>
            <CardDescription>
              إجمالي السجلات: {totalRows.toLocaleString("ar-EG")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
              {Object.entries(stats)
                .filter(([, c]) => c > 0)
                .sort(([, a], [, b]) => b - a)
                .map(([t, c]) => (
                  <div
                    key={t}
                    className="flex items-center justify-between p-2 rounded bg-muted/50"
                  >
                    <span className="text-muted-foreground text-xs truncate">{t}</span>
                    <Badge variant="outline">{c}</Badge>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Restore / Validate */}
      <Card className="border-blue-200/60">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSearch className="w-5 h-5 text-blue-600" /> التحقق من ملف نسخة احتياطية
          </CardTitle>
          <CardDescription>
            ارفع ملف JSON لاختبار سلامته قبل الاستعادة. لن يتم تطبيق أي تغيير على البيانات في هذه المرحلة.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={handleFileSelect}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={validating}
            >
              {validating ? (
                <><Loader2 className="w-4 h-4 ml-2 animate-spin" /> جاري الفحص...</>
              ) : (
                <><Upload className="w-4 h-4 ml-2" /> اختر ملف JSON للفحص</>
              )}
            </Button>
            {report && (
              <Button variant="ghost" onClick={resetReport}>
                <RotateCcw className="w-4 h-4 ml-2" /> إعادة تعيين
              </Button>
            )}
          </div>

          {report && (
            <div className="space-y-3">
              {/* Status banner */}
              <div
                className={`flex items-start gap-3 p-4 rounded-lg border ${
                  report.isValid
                    ? "bg-green-50 border-green-200 dark:bg-green-950/20"
                    : "bg-red-50 border-red-200 dark:bg-red-950/20"
                }`}
              >
                {report.isValid ? (
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className="font-semibold">
                    {report.isValid ? "✅ الملف سليم — جاهز للاستعادة" : "❌ الملف يحتوي على أخطاء"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {report.fileName} • {report.totalTables} جدول • {report.totalRows.toLocaleString("ar-EG")} سجل
                  </p>
                </div>
                <div className="flex gap-2">
                  <Badge variant={report.errorCount > 0 ? "destructive" : "outline"}>
                    {report.errorCount} خطأ
                  </Badge>
                  <Badge variant={report.warningCount > 0 ? "secondary" : "outline"}>
                    {report.warningCount} تحذير
                  </Badge>
                </div>
              </div>

              {/* Meta info */}
              {report.meta && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="p-2 rounded bg-muted/40">
                    <p className="text-muted-foreground">الحضانة</p>
                    <p className="font-medium truncate">{report.meta.nursery_name || "—"}</p>
                  </div>
                  <div className="p-2 rounded bg-muted/40">
                    <p className="text-muted-foreground">إصدار النسخة</p>
                    <p className="font-medium">{report.meta.version ?? "—"}</p>
                  </div>
                  <div className="p-2 rounded bg-muted/40">
                    <p className="text-muted-foreground">تاريخ التصدير</p>
                    <p className="font-medium">
                      {report.meta.exported_at
                        ? format(new Date(report.meta.exported_at), "dd/MM/yyyy HH:mm")
                        : "—"}
                    </p>
                  </div>
                  <div className="p-2 rounded bg-muted/40">
                    <p className="text-muted-foreground">عدد الجداول</p>
                    <p className="font-medium">{report.totalTables}</p>
                  </div>
                </div>
              )}

              {/* Per-table summary */}
              {Object.keys(report.tableSummary).length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">ملخص حسب الجدول:</p>
                  <ScrollArea className="h-48 rounded border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 p-2 text-xs">
                      {Object.entries(report.tableSummary)
                        .sort(([, a], [, b]) => b.errors - a.errors || b.warnings - a.warnings)
                        .map(([t, s]) => (
                          <div
                            key={t}
                            className={`flex items-center justify-between p-2 rounded ${
                              s.errors > 0
                                ? "bg-red-50 dark:bg-red-950/20"
                                : s.warnings > 0
                                ? "bg-amber-50 dark:bg-amber-950/20"
                                : "bg-muted/40"
                            }`}
                          >
                            <span className="truncate">{t}</span>
                            <div className="flex gap-1">
                              <Badge variant="outline" className="text-[10px] px-1">
                                {s.rows} سجل
                              </Badge>
                              {s.errors > 0 && (
                                <Badge variant="destructive" className="text-[10px] px-1">
                                  {s.errors} خطأ
                                </Badge>
                              )}
                              {s.warnings > 0 && (
                                <Badge variant="secondary" className="text-[10px] px-1">
                                  {s.warnings} تحذير
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Issues list */}
              {report.issues.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold flex items-center gap-2">
                      <FileWarning className="w-4 h-4" />
                      تقرير الأخطاء والتحذيرات ({report.issues.length})
                    </p>
                    {report.issues.length > 20 && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setShowAllIssues((v) => !v)}
                      >
                        {showAllIssues ? "إظهار أقل" : `عرض الكل (${report.issues.length})`}
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-64 rounded border">
                    <div className="p-2 space-y-1">
                      {(showAllIssues ? report.issues : report.issues.slice(0, 20)).map(
                        (iss, i) => (
                          <div
                            key={i}
                            className={`flex items-start gap-2 p-2 rounded text-xs ${
                              iss.severity === "error"
                                ? "bg-red-50 dark:bg-red-950/20"
                                : iss.severity === "warning"
                                ? "bg-amber-50 dark:bg-amber-950/20"
                                : "bg-muted/40"
                            }`}
                          >
                            {iss.severity === "error" ? (
                              <XCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                            ) : (
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1">
                              <p>{iss.message}</p>
                              {(iss.table || iss.field || iss.rowIndex !== undefined) && (
                                <p className="text-muted-foreground text-[10px] mt-0.5">
                                  {iss.table && `جدول: ${iss.table}`}
                                  {iss.rowIndex !== undefined && ` • سجل #${iss.rowIndex}`}
                                  {iss.field && ` • حقل: ${iss.field}`}
                                </p>
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Auto-fix panel */}
              {(report.errorCount > 0 || report.warningCount > 0 || autoFixReport) && (
                <div className="rounded-lg border border-purple-200 bg-purple-50/40 dark:bg-purple-950/10 p-3 space-y-3">
                  <div className="flex items-start gap-3">
                    <Wand2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-sm flex items-center gap-2">
                        إصلاح تلقائي للأخطاء الشائعة
                        {autoFixReport && (
                          <Badge variant="secondary" className="text-[10px]">
                            <Sparkles className="w-3 h-3 ml-1" />
                            {autoFixReport.totalChanges} إصلاح
                          </Badge>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        يحاول إصلاح: تنسيق التواريخ، تطبيع UUID، تحويل الأرقام/المنطقية، إفراغ الحقول الفارغة،
                        توليد المعرّفات المفقودة، وإزالة المكرر — قبل الاستعادة.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={runAutoFix}
                      disabled={autoFixing || !rawParsed}
                    >
                      {autoFixing ? (
                        <><Loader2 className="w-3.5 h-3.5 ml-2 animate-spin" /> جاري الإصلاح...</>
                      ) : (
                        <><Wand2 className="w-3.5 h-3.5 ml-2" /> {autoFixReport ? "إعادة التشغيل" : "تشغيل الإصلاح التلقائي"}</>
                      )}
                    </Button>
                  </div>

                  {autoFixReport && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="p-2 rounded bg-background border">
                          <p className="text-muted-foreground">إجمالي التغييرات</p>
                          <p className="font-semibold text-lg">{autoFixReport.totalChanges}</p>
                        </div>
                        <div className="p-2 rounded bg-background border">
                          <p className="text-muted-foreground">سجلات تم إصلاحها</p>
                          <p className="font-semibold text-lg text-green-600">{autoFixReport.fixedRows}</p>
                        </div>
                        <div className="p-2 rounded bg-background border">
                          <p className="text-muted-foreground">سجلات مكررة محذوفة</p>
                          <p className="font-semibold text-lg text-amber-600">{autoFixReport.droppedRows}</p>
                        </div>
                      </div>

                      {autoFixReport.issues.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-semibold">عينة من الإصلاحات المُطبَّقة:</p>
                            {autoFixReport.issues.length > 10 && (
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs"
                                onClick={() => setShowAllFixes((v) => !v)}
                              >
                                {showAllFixes ? "إظهار أقل" : `عرض الكل (${autoFixReport.issues.length})`}
                              </Button>
                            )}
                          </div>
                          <ScrollArea className="h-40 rounded border bg-background">
                            <div className="p-2 space-y-1">
                              {(showAllFixes ? autoFixReport.issues : autoFixReport.issues.slice(0, 10)).map((iss, i) => (
                                <div key={i} className="flex items-start gap-2 p-2 rounded bg-muted/40 text-[11px]">
                                  <Sparkles className="w-3 h-3 text-purple-600 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1">
                                    <p className="font-medium">{iss.reason}</p>
                                    <p className="text-muted-foreground mt-0.5">
                                      {iss.table}
                                      {iss.field && ` • ${iss.field}`}
                                      {iss.rowIndex !== undefined && ` • سجل #${iss.rowIndex}`}
                                    </p>
                                    <p className="text-muted-foreground truncate">
                                      <span className="text-red-600">{String(iss.before ?? "—").slice(0, 40)}</span>
                                      {" → "}
                                      <span className="text-green-700">{String(iss.after ?? "null").slice(0, 40)}</span>
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Restore controls */}
              <div className="space-y-3 pt-3 border-t">
                <div>
                  <Label className="text-sm font-semibold mb-2 block">
                    وضع الاستعادة:
                  </Label>
                  <div className="grid md:grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setRestoreMode("dry_run")}
                      className={`text-right p-3 rounded-lg border-2 transition-colors ${
                        restoreMode === "dry_run"
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                          : "border-border hover:border-blue-200"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <PlayCircle className="w-4 h-4 text-blue-600" />
                        <span className="font-semibold text-sm">اختبار جاف (Dry-Run)</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        يحاكي الاستعادة بدون أي كتابة على البيانات. يُنصح بتشغيله أولاً.
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRestoreMode("merge")}
                      className={`text-right p-3 rounded-lg border-2 transition-colors ${
                        restoreMode === "merge"
                          ? "border-amber-500 bg-amber-50 dark:bg-amber-950/20"
                          : "border-border hover:border-amber-200"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldAlert className="w-4 h-4 text-amber-600" />
                        <span className="font-semibold text-sm">دمج (Merge / Upsert)</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        يحدّث السجلات الموجودة (نفس الـ id) ويضيف الجديد. لا يحذف شيئًا.
                      </p>
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                  <Button
                    disabled={!report.isValid || restoring}
                    onClick={() => {
                      if (restoreMode === "dry_run") runRestore();
                      else setConfirmOpen(true);
                    }}
                  >
                    {restoring ? (
                      <><Loader2 className="w-4 h-4 ml-2 animate-spin" /> جاري...</>
                    ) : (
                      <><RotateCcw className="w-4 h-4 ml-2" />
                        {restoreMode === "dry_run" ? "تشغيل الاختبار الجاف" : "تنفيذ الاستعادة"}
                      </>
                    )}
                  </Button>
                  {!report.isValid && (
                    <p className="text-xs text-red-600">
                      لا يمكن الاستعادة قبل إصلاح جميع الأخطاء.
                    </p>
                  )}
                </div>

                {restoring && (
                  <div className="space-y-2 p-3 rounded bg-muted/40">
                    <div className="flex justify-between text-xs">
                      <span>جاري معالجة: {restoreCurrentTable || "..."}</span>
                      <span>{restoreProgress}%</span>
                    </div>
                    <Progress value={restoreProgress} />
                  </div>
                )}

                {restoreResult && (
                  <div
                    className={`p-3 rounded-lg border space-y-2 ${
                      restoreResult.totalFailed === 0
                        ? "bg-green-50 border-green-200 dark:bg-green-950/20"
                        : "bg-amber-50 border-amber-200 dark:bg-amber-950/20"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {restoreResult.totalFailed === 0 ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      )}
                      <p className="font-semibold text-sm">
                        {restoreResult.mode === "dry_run"
                          ? `معاينة: ${restoreResult.totalUpserted} سجل سيتم استعادته`
                          : `النتيجة: ${restoreResult.totalUpserted} نجاح / ${restoreResult.totalFailed} فشل`}
                      </p>
                    </div>
                    <ScrollArea className="h-40 rounded border bg-background">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="text-right p-2">الجدول</th>
                            <th className="text-center p-2">الإجمالي</th>
                            <th className="text-center p-2 text-green-600">نجاح</th>
                            <th className="text-center p-2 text-red-600">فشل</th>
                          </tr>
                        </thead>
                        <tbody>
                          {restoreResult.tables.map((t) => (
                            <tr key={t.table} className="border-t">
                              <td className="p-2">{t.table}</td>
                              <td className="text-center p-2">{t.total}</td>
                              <td className="text-center p-2 text-green-600">{t.upserted}</td>
                              <td className="text-center p-2 text-red-600">{t.failed}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                    {restoreResult.tables.some((t) => t.errors.length > 0) && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-red-600 font-semibold">
                          عرض رسائل الخطأ
                        </summary>
                        <div className="mt-2 space-y-1 max-h-32 overflow-auto">
                          {restoreResult.tables
                            .filter((t) => t.errors.length > 0)
                            .map((t) => (
                              <div key={t.table} className="p-2 bg-red-50 dark:bg-red-950/20 rounded">
                                <p className="font-semibold">{t.table}:</p>
                                {t.errors.slice(0, 3).map((e, i) => (
                                  <p key={i} className="text-muted-foreground">• {e}</p>
                                ))}
                              </div>
                            ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>

            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-amber-200 bg-amber-50/40 dark:bg-amber-950/10">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-2 text-sm">
              <p className="font-semibold">نصائح مهمة:</p>
              <ul className="list-disc pr-5 space-y-1 text-muted-foreground">
                <li>احتفظ بالنسخة في مكان آمن (هارد خارجي / Google Drive).</li>
                <li>يُفضّل عمل نسخة احتياطية أسبوعيًا على الأقل.</li>
                <li>الملف يحتوي على بيانات حساسة — لا تشاركه مع أحد.</li>
                <li>الصور والملفات المرفوعة (الفعليّة) لا تُحفظ في النسخة، فقط روابطها.</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Confirmation dialog for real restore */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="w-5 h-5" /> تأكيد الاستعادة
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-right">
              <span className="block">
                ستُكتب البيانات من النسخة فوق السجلات الحالية ذات نفس المعرّف.
                هذه العملية <strong className="text-destructive">لا يمكن التراجع عنها</strong>.
              </span>
              <span className="block">
                للمتابعة، اكتب كلمة <strong>تأكيد</strong> في الحقل أدناه:
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="اكتب: تأكيد"
            dir="rtl"
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmText("")}>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText.trim() !== "تأكيد"}
              onClick={runRestore}
              className="bg-destructive hover:bg-destructive/90"
            >
              تنفيذ الاستعادة
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Backup;
